<template>
  <div class="contracts-show-view">
    <loader v-if="isLoading" />
    <div v-if="isSuccessfullyLoaded">
      <h1>{{ contract.name }}</h1>

      <div class="action-buttons-container">
        <div class="action-buttons">

          <link-button :to="{ name: 'edit-contract', params: { id: contract.id } }" icon="pencil">
            {{ $t('actions.edit') }}
          </link-button>

          <link-button :to="{ name: 'contract-revisions', params: { id: contract.id } }" icon="future">
            {{ $t('actions.editRevisions') }}
          </link-button>


          <common-button @click="deleteContractWhenConfirmed" type="danger" icon="trash">
            {{ $t('actions.delete') }}
          </common-button>
        </div>

        <div class="active-revision-indicator" v-if="hasActiveRevision">
          <icon name="check" />
          <b> {{ $t('hints.activeRevision') }} </b>
        </div>
      </div>

      <mini-gallery :record="contract" />

      <h3>{{ $t('headlines.attributes') }}</h3>

      <attribute-table :data="contract"
        :fields="['contractNumber', 'provider', 'contractDuration', 'insertedAt', 'updatedAt']">
        <template slot="provider">
          <provider-name :name="contract.provider" />
        </template>
        <template slot="contractDuration">
          <duration v-if="contract.contractDuration" :value="contract.contractDuration" />
          <text-placeholder v-else />
        </template>
        <template slot="insertedAt">
          <timeago :since="contract.insertedAt" :auto-update="60" />
        </template>
        <template slot="updatedAt">
          <timeago :since="contract.updatedAt" :auto-update="60" />
        </template>
      </attribute-table>
    </div>
  </div>
</template>

<script>
import UIkit from 'uikit';
import AttributeTable from '@/components/AttributeTable';
import CommonButton from '@/components/CommonButton';
import contractQuery from '@/api/queries/contract.graphql';
import deleteContractMutation from '@/api/mutations/delete-contract.graphql';
import Duration from '@/components/Duration';
import MiniGallery from '@/components/MiniGallery';
import ProviderName from '@/components/ProviderName';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';
import Icon from '@/components/Icon';

export default {
  name: 'contracts-show-view',
  mixins: [View],
  components: {
    AttributeTable,
    CommonButton,
    Duration,
    MiniGallery,
    ProviderName,
    TextPlaceholder,
    Icon,
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    breadcrumbs() {
      return [
        {
          key: 'show',
          text: () => this.contract.name,
          route: { name: 'contract', params: { id: this.id } },
          isLoading: !this.isSuccessfullyLoaded,
        },
      ];
    },
    images() {
      return this.contract.media.filter(m => m.type === 'IMAGE');
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.contract;
    },
    contractType() {
      const contract = this.contract;
      if (contract.product && contract.contract) {
        return this.$t('contractTypes.productWithContract');
      } else if (contract.product) {
        return this.$t('contractTypes.productOnly');
      }
      return this.$t('contractTypes.contractOnly');
    },
    hasActiveRevision() {
      return this.contract.activeRevision ?
        this.contract.activeRevision.active : false;
    },
  },
  apollo: {
    contract: {
      query: contractQuery,
      variables() {
        return { id: this.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update({ contract }) {
        return contract;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  methods: {
    deleteContractWhenConfirmed() {
      UIkit.modal
        .confirm(this.$t('confirmations.delete'))
        .then(() => { this.deleteContract(); })
        .catch(() => { });
    },
    async deleteContract() {
      await this.$apollo.mutate({
        mutation: deleteContractMutation,
        variables: { id: this.contract.id },
      });

      const message = this.$t('toasts.deleted');
      Toast.success(message);
      this.$router.push({ name: 'contracts' });
    },
  },
  i18n: {
    messages: {
      de: {
        confirmations: {
          delete: `Angebot wirklich löschen? Alle zugehörigen Preise und
                   Freischaltungen für Mandanten werden ebenfalls entfernt.`,
        },
        headlines: {
          attributes: 'Eigenschaften',
          prices: 'Preise',
        },
        hints: {
          withoutProduct: 'ohne Produkt',
          withoutContract: 'ohne Vertrag',
          activeRevision: 'Hier ist eine aktive Überarbeitung.',
        },
        contractTypes: {
          productOnly: 'Solo-Produkt',
          contractOnly: 'Solo-Vertrag',
          productWithContract: 'Produkt mit Vertrag',
        },
        toasts: {
          deleted: 'Angebot wurde gelöscht',
        },
      },
    },
  },
};
</script>

<style lang="scss">
.action-buttons-container {
  display: flex;
  align-items: center;
  /* Align items vertically */
}

.action-buttons {
  display: flex;
  gap: 10px;
  /* Adjust spacing between buttons */
}

.active-revision-indicator {
  margin-left: auto;
  /* Push the "ACTIVE" indicator to the right */
  padding-right: 10px;
}
</style>



// WEBPACK FOOTER //
// ShowView.vue?1b472c8e